import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useSearchSuggestions, useProduct} from '@salesforce/commerce-sdk-react'
import {
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Image,
    Link,
    IconButton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PopoverArrow, useOutsideClick} from '@chakra-ui/react'
import {CloseIcon} from '@chakra-ui/icons'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import {
    suggestionsHeaderStyle,
    phraseLinkStyle,
    featuredHeaderStyle,
    productsRowStyle,
    productCardStyle,
    productImageStyle,
    productNameStyle,
    productPriceStyle,
    productImageMobile,
    productCardMobileStyle,
    containerStyle,
    searchInputStyle,
    inputLeftElementStyle,
    popoverDesktopContentStyle,
    popoverArrowStyle,
    mobileDropdownStaticStyle,
    mobileDropdownArrowStaticStyle,
    mobileDropdownBoxStyle,
    wrapperBoxStyle,
    inputRightElementStyle,
    clearButtonStyle
} from './index.style'
import {pxToRem} from '../../utills/pxToRem'
import {RECENT_SEARCH_MIN_LENGTH} from '@salesforce/retail-react-app/app/constants'
import {capitalize, boldString} from '@salesforce/retail-react-app/app/utils/utils'
import {FormattedNumber, useIntl} from 'react-intl'
import {searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'

const getProductLink = (product) => {
    if (!product) return ''
    if (product.c_productURL) return product.c_productURL.replace('/s/Cavenders', '')
    const slug = product.c_slug || product.name?.toLowerCase().replace(/\s+/g, '-') || product.id
    return `/products/${slug}/${product.id}.html`
}

const SearchV2 = ({onClearAndClose, autoFocus, ...props}) => {
    const intl = useIntl()
    const [inputValue, setInputValue] = useState('')
    const [query, setQuery] = useState('')
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false)
    const searchInputRef = useRef(null)
    const popoverContainerRef = useRef(null)

    useOutsideClick({
        ref: popoverContainerRef,
        handler: () => setIsSuggestionsOpen(false)
    })

    const debouncedSetQuery = useRef(debounce((val) => setQuery(val), 300)).current

    const suggestionsResult = useSearchSuggestions(
        {parameters: {q: query, limit: 3, locale: 'default'}},
        {
            enabled: query.length >= RECENT_SEARCH_MIN_LENGTH,
            keepPreviousData: query.length >= RECENT_SEARCH_MIN_LENGTH
        }
    )
    const searchLocation = suggestionsResult?.data?.customSuggestions?.c_redirectURL
    const normalizedQuery = inputValue.trim().toUpperCase()
    const looksLikeId = /^[A-Za-z0-9]{6,}$/.test(normalizedQuery)

    const {data: productById} = useProduct(
        {parameters: {id: normalizedQuery, allImages: true, allVariationProperties: true}},
        {enabled: looksLikeId}
    )

    const formatResults = useCallback((data, rawInput) => {
        const phrases = (data?.customSuggestions?.suggestedPhrases ?? []).map((p) =>
            boldString(p.phrase, capitalize(rawInput))
        )
        const products = (data?.productSuggestions?.products ?? []).map((item) => ({
            id: item.productId,
            title: boldString(item.productName, capitalize(rawInput)),
            price: item.price || '',
            currency: item.currency || '',
            link: getProductLink(item),
            imageUrl: item.c_productImage?.src || item.c_cloudinary?.url || ''
        }))
        return {phraseResults: phrases, productResults: products}
    }, [])

    const {phraseResults, productResults} = useMemo(() => {
        const raw = searchInputRef.current?.value || ''
        return formatResults(suggestionsResult.data, raw)
    }, [suggestionsResult.data, formatResults])

    const finalProductResults = useMemo(() => {
        if (productResults.length > 0) return productResults
        if (productById) {
            return [
                {
                    id: productById.id,
                    title: productById.name,
                    link: getProductLink(productById),
                    price: productById.price || '',
                    currency: productById.currency || '',
                    imageUrl:
                        productById.image?.link ||
                        productById.c_productImage?.src ||
                        productById.c_cloudinary?.url ||
                        ''
                }
            ]
        }
        return []
    }, [productResults, productById])

    const handleInputChange = useCallback(
        (e) => {
            const v = e.target.value
            setInputValue(v)
            const t = v.trim()
            if (t.length >= RECENT_SEARCH_MIN_LENGTH) debouncedSetQuery(t)
            else {
                debouncedSetQuery.cancel()
                setQuery('')
            }
        },
        [debouncedSetQuery]
    )

    const handleFormSubmit = useCallback(
        async (e) => {
            e.preventDefault()
            const term = inputValue?.trim().toUpperCase()
            if (term.length < RECENT_SEARCH_MIN_LENGTH) return
            let location = searchLocation
            if (location == null) {
                const {data} = await suggestionsResult.refetch({queryKeyOverride: [term]})
                location = data?.customSuggestions?.c_redirectURL
            }
            if (productById && productById.id.toUpperCase() === term)
                window.location.href = getProductLink(productById)
            else if (location) window.location.href = location
            else window.location.href = searchUrlBuilder(term)
        },
        [inputValue, productById, searchLocation, suggestionsResult]
    )

    const handleLink = () => {
        setIsSuggestionsOpen(false)
        setInputValue('')
    }

    useEffect(() => {
        setIsSuggestionsOpen(inputValue.trim().length >= RECENT_SEARCH_MIN_LENGTH)
    }, [inputValue])

    useEffect(() => {
        return () => {
            debouncedSetQuery.cancel()
        }
    }, [debouncedSetQuery])

    const handleClear = useCallback(() => {
        setInputValue('')
        debouncedSetQuery.cancel()
        setQuery('')
        setIsSuggestionsOpen(false)
        onClearAndClose?.()
        // Focus input only when still mounted (e.g. desktop); parent may unmount on mobile clear
        requestAnimationFrame(() => searchInputRef.current?.focus())
    }, [debouncedSetQuery, onClearAndClose])

    useEffect(() => {
        if (!autoFocus) return
        const id = setTimeout(() => searchInputRef.current?.focus(), 100)
        return () => clearTimeout(id)
    }, [autoFocus])

    return (
        <Box sx={containerStyle} ref={popoverContainerRef}>
            <Popover
                isOpen={isSuggestionsOpen}
                onClose={() => setIsSuggestionsOpen(false)}
                placement="bottom-start"
                arrowSize={13}
                closeOnEsc={false}
                isLazy
                initialFocusRef={searchInputRef}
                sx={{
                    '& [data-popper-arrow], & .chakra-popover__arrow-positioner': {
                        left: `-${pxToRem(60)} !important`
                    }
                }}
            >
                <PopoverTrigger>
                    <form onSubmit={handleFormSubmit} autoComplete="off">
                        <HStack columnGap={0}>
                            <InputGroup>
                                <InputLeftElement sx={inputLeftElementStyle}>
                                    <Box
                                        as="img"
                                        src={getAssetUrl('static/img/header-v2/search-icon.svg')}
                                        alt=""
                                        width={20}
                                        height={20}
                                        sx={{
                                            display: 'block',
                                            width: {
                                                base: pxToRem(20),
                                                sm: pxToRem(20),
                                                md: 5,
                                                lg: 5
                                            },
                                            height: {
                                                base: pxToRem(20),
                                                sm: pxToRem(20),
                                                md: 5,
                                                lg: 5
                                            },
                                            objectFit: 'contain'
                                        }}
                                    />
                                </InputLeftElement>
                                <Input
                                    id="search-input"
                                    ref={searchInputRef}
                                    type="search"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) =>
                                        e.key === 'Escape' &&
                                        (e.preventDefault(), setIsSuggestionsOpen(false))
                                    }
                                    focusBorderColor="white"
                                    {...props}
                                    sx={searchInputStyle}
                                />
                                <InputRightElement sx={inputRightElementStyle}>
                                    {inputValue ? (
                                        <IconButton
                                            aria-label={intl.formatMessage({
                                                id: 'header.search.clear',
                                                defaultMessage: 'Clear search'
                                            })}
                                            icon={<CloseIcon boxSize={pxToRem(12)} />}
                                            onClick={handleClear}
                                            variant="unstyled"
                                            sx={clearButtonStyle}
                                        />
                                    ) : null}
                                </InputRightElement>
                            </InputGroup>
                        </HStack>
                    </form>
                </PopoverTrigger>
                {isSuggestionsOpen && (phraseResults.length || productResults.length) > 0 && (
                    <PopoverContent sx={popoverDesktopContentStyle}>
                        <PopoverArrow {...popoverArrowStyle} />
                        <Box sx={wrapperBoxStyle}>
                            {phraseResults.length > 0 && (
                                <>
                                    <Box sx={suggestionsHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.suggestions',
                                            defaultMessage: 'SUGGESTIONS'
                                        })}
                                    </Box>
                                    <VStack align="stretch" spacing={0}>
                                        {phraseResults.map((p, i) => (
                                            <Link
                                                key={i}
                                                sx={phraseLinkStyle}
                                                href={searchUrlBuilder(p.replace(/<[^>]+>/g, ''))}
                                                onClick={handleLink}
                                            >
                                                {p.replace(/<[^>]+>/g, '')}
                                            </Link>
                                        ))}
                                    </VStack>
                                </>
                            )}
                            {finalProductResults.length > 0 && (
                                <>
                                    <Box sx={featuredHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.featured_products',
                                            defaultMessage: 'FEATURED PRODUCTS'
                                        })}
                                    </Box>
                                    <Flex sx={productsRowStyle}>
                                        {finalProductResults.map((prod) => {
                                            const title = prod.title.replace(/<[^>]+>/g, '')
                                            return (
                                                <Link
                                                    key={prod.id}
                                                    sx={productCardStyle}
                                                    href={prod.link}
                                                    onClick={handleLink}
                                                >
                                                    <Image
                                                        sx={productImageStyle}
                                                        src={prod.imageUrl}
                                                        alt=""
                                                    />
                                                    <Text sx={productNameStyle}>{title}</Text>
                                                    <Text sx={productPriceStyle}>
                                                        <FormattedNumber
                                                            value={prod.price}
                                                            style="currency"
                                                            currency={prod.currency}
                                                        />
                                                    </Text>
                                                </Link>
                                            )
                                        })}
                                    </Flex>
                                </>
                            )}
                        </Box>
                    </PopoverContent>
                )}
                {isSuggestionsOpen && (phraseResults.length || productResults.length) > 0 && (
                    <Box sx={mobileDropdownStaticStyle}>
                        <Box sx={mobileDropdownArrowStaticStyle} />
                        <Box sx={mobileDropdownBoxStyle}>
                            {phraseResults.length > 0 && (
                                <>
                                    <Box sx={suggestionsHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.suggestions',
                                            defaultMessage: 'SUGGESTIONS'
                                        })}
                                    </Box>
                                    <VStack align="stretch" spacing={pxToRem(8)} mb={pxToRem(16)}>
                                        {phraseResults.map((p, i) => (
                                            <Link
                                                key={i}
                                                sx={phraseLinkStyle}
                                                href={searchUrlBuilder(p.replace(/<[^>]+>/g, ''))}
                                                onClick={handleLink}
                                            >
                                                {p.replace(/<[^>]+>/g, '')}
                                            </Link>
                                        ))}
                                    </VStack>
                                </>
                            )}
                            {finalProductResults.length > 0 && (
                                <>
                                    <Box sx={featuredHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.featured_products',
                                            defaultMessage: 'FEATURED PRODUCTS'
                                        })}
                                    </Box>
                                    <VStack spacing={pxToRem(16)} mt={pxToRem(22)}>
                                        {finalProductResults.map((prod) => {
                                            const title = prod.title.replace(/<[^>]+>/g, '')
                                            return (
                                                <Link
                                                    key={prod.id}
                                                    sx={productCardMobileStyle}
                                                    href={prod.link}
                                                    onClick={handleLink}
                                                >
                                                    <Image
                                                        sx={productImageMobile}
                                                        src={prod.imageUrl}
                                                        alt=""
                                                    />
                                                    <Box ml={pxToRem(20)} mt={pxToRem(6)} flex={1}>
                                                        <Text sx={productNameStyle} noOfLines={2}>
                                                            {title}
                                                        </Text>
                                                        <Text sx={productPriceStyle}>
                                                            <FormattedNumber
                                                                value={prod.price}
                                                                style="currency"
                                                                currency={prod.currency}
                                                            />
                                                        </Text>
                                                    </Box>
                                                </Link>
                                            )
                                        })}
                                    </VStack>
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </Popover>
        </Box>
    )
}

SearchV2.displayName = 'SearchV2'

SearchV2.propTypes = {
    onClearAndClose: PropTypes.func,
    autoFocus: PropTypes.bool
}

export default SearchV2
